import { useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../lib/api.js';
import { askConfirm, errorText, showError } from '../lib/dialogs.js';
import { useFetch } from '../lib/useFetch.js';
import { fetchNoteUsers } from '../lib/noteUsers.js';
import { TASKS_REMOTE } from '../lib/liveEvents.js';
import { useLiveRefresh } from '../lib/useLiveRefresh.js';
import { isOpenTask } from '@ehr/shared';
import { useSession, canManage, displayName } from '../auth/session.js';
import { DateField, TimeField } from '../components/DateTimeField.js';
import { ConfirmDialog } from '../components/ConfirmDialog.js';
import { ArchiveIcon, RestoreButton, ShowArchivedToggle } from '../components/ArchiveControls.js';
import { archiveItem, archivedUrl, restoreItem } from '../lib/chartArchive.js';
import { relabel, useBranding } from '../lib/branding.js';
import {
  NoteAccessToggle,
  NoteSharePopover,
  ShareIcon,
  UploadShareIcon,
  nsAvatarColor,
  nsInitials,
  nsRoleLabel,
  nsUserName,
  noteShares,
  type NoteAccess,
  type NoteShare,
} from '../components/noteSharing.js';
import {
  RecurrenceField,
  NO_RECURRENCE,
  repeatHint,
  type Recurrence,
} from '../components/RecurrenceField.js';
import { Dd } from '../components/Dropdown.js';
import { TaskListView, TaskBoardView, type TaskView } from '../components/taskViews.js';
import { useTaskStatuses } from '../lib/taskStatuses.js';
import { ownTask } from '../components/share/tabAccess.js';

type TaskStatus = 'open' | 'in_progress' | 'done' | 'cancelled';
interface Task {
  id: string;
  title: string;
  priority: 'urgent' | 'high' | 'normal' | 'low';
  status: TaskStatus;
  dueAt: string | null;
  doneAt: string | null;
  contactId: string | null;
  createdById: string;
  assignees: { userId: string }[];
  sharedWith?: string[];
  checklist?: { done: boolean }[]; // ehr#487: progress on the row
}
/** A task is "closed" when it isn't open (ehr#659: @ehr/shared isOpenTask, the rule every surface uses). */
const isClosed = (t: Task) => !isOpenTask(t);
type Sort = 'due_asc' | 'due_desc' | 'priority' | 'title';
type LinkGroup = 'all' | 'client' | 'partner';

// Task filter — two axes (who × state), ported from EHR_08-08-V34.html. The six
// default chips are just six cells of this 3×4 grid; "+ More" opens the rest and
// lets you pin/unpin cells to the chip strip.
type TaskWho = 'anyone' | 'me' | 'assigned';
type TaskState = 'any' | 'open' | 'overdue' | 'done';
type WsFilter = [TaskWho, TaskState];
const TASK_WHO: { id: TaskWho; label: string }[] = [
  { id: 'anyone', label: 'Anyone' },
  { id: 'me', label: 'Me' },
  { id: 'assigned', label: 'I assigned out' },
];
const TASK_STATE: { id: TaskState; label: string }[] = [
  { id: 'any', label: 'Any' },
  { id: 'open', label: 'Open' },
  { id: 'overdue', label: 'Overdue' },
  { id: 'done', label: 'Closed' },
];
const TASK_FILTER_NICE: Record<string, string> = {
  'anyone|any': 'All',
  'anyone|open': 'All open',
  'anyone|overdue': 'Overdue',
  'anyone|done': 'Completed',
  'me|open': 'My open',
  'me|overdue': 'My overdue',
  'me|done': 'My closed',
  'me|any': 'All mine',
  'assigned|open': 'I assigned',
  'assigned|overdue': 'I assigned · overdue',
  'assigned|done': 'I assigned · closed',
  'assigned|any': 'I assigned · all',
};
// ehr#223 (Taras): "All open" sits right after "All" and is the filter the page opens on.
const TASK_DEFAULT_FILTER: WsFilter = ['anyone', 'open'];
const TASK_PIN_DEFAULTS: WsFilter[] = [
  ['anyone', 'any'],
  TASK_DEFAULT_FILTER,
  ['me', 'open'],
  ['anyone', 'overdue'],
  ['assigned', 'open'],
  ['anyone', 'done'],
];
const wsKey = (w: TaskWho, s: TaskState) => `${w}|${s}`;
const wsLabel = (w: TaskWho, s: TaskState) =>
  TASK_FILTER_NICE[wsKey(w, s)] ??
  `${TASK_WHO.find((x) => x.id === w)?.label ?? w} · ${TASK_STATE.find((x) => x.id === s)?.label ?? s}`;
/** One predicate for every who×state cell (binary done/open model). */
const taskMatchesWs = (
  t: Task,
  myId: string | undefined,
  who: TaskWho,
  state: TaskState,
): boolean => {
  // "Mine" = assigned to me OR shared with me (#23). Without the shared clause a
  // task someone shares with me never surfaced in my "Me / My open" views, even
  // though the API returns it — so sharing looked broken for the recipient.
  const mine =
    !!myId &&
    (t.assignees.some((a) => a.userId === myId) || (t.sharedWith?.includes(myId) ?? false));
  const okWho = who === 'anyone' ? true : who === 'me' ? mine : t.createdById === myId && !mine;
  if (!okWho) return false;
  const closed = isClosed(t);
  if (state === 'any') return true;
  if (state === 'open') return !closed;
  if (state === 'done') return closed;
  return !closed && !!t.dueAt && taskDueDiff(t.dueAt) < 0; // overdue
};

const PRIORITY_RANK: Record<string, number> = { urgent: 0, high: 1, normal: 2, low: 3 };
const PRIORITY_COLS: { key: string; label: string; dot: string }[] = [
  { key: 'urgent', label: 'Urgent', dot: 'var(--red)' },
  { key: 'high', label: 'High', dot: 'var(--caution)' },
  { key: 'normal', label: 'Normal', dot: 'var(--green-primary)' },
  { key: 'low', label: 'Low', dot: 'var(--text-muted-2)' },
];

/** Whole-day diff (task due day − today). Negative = overdue. */
const taskDueDiff = (iso: string) =>
  Math.round((dayFloor(new Date(iso)) - dayFloor(new Date())) / 86400_000);
const dayFloor = (d: Date) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x.getTime();
};
// ── Task drawer (V27 mockup parity) — new + edit ────────────────────────────
interface OrgUser {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  role?: string;
}
interface LinkContact {
  id: string;
  firstName: string;
  lastName: string;
  category: string;
}
const AVATAR_COLORS = ['#7c3aed', '#0c7be0', '#0e7490', '#d97706', '#1a8a66', '#c0392b', '#527060'];
const avatarColor = (id: string) => {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length]!;
};
const uName = (u: OrgUser) => [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email;
const uInitials = (u: OrgUser) => {
  const a = (u.firstName || '').trim();
  const b = (u.lastName || '').trim();
  return ((a[0] || '') + (b[0] || '')).toUpperCase() || (u.email[0] || '?').toUpperCase();
};
const PRIORITY_OPTS = [
  { value: 'urgent', label: 'Urgent', dot: 'var(--red)' },
  { value: 'high', label: 'High', dot: 'var(--caution)' },
  { value: 'normal', label: 'Normal', dot: '#527060' },
  { value: 'low', label: 'Low', dot: 'var(--text-muted-2)' },
];

/** MM/DD/YYYY → ISO 'YYYY-MM-DD' for the DateField picker; '' when incomplete. */
const mdyToISO = (v: string) => {
  const m = v.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  return m ? `${m[3]}-${m[1]}-${m[2]}` : '';
};
/** ISO 'YYYY-MM-DD' (from DateField) → MM/DD/YYYY; '' when empty/invalid. */
const isoToMdy = (v: string) => {
  const m = v.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : '';
};
/** Today, local, as ISO 'YYYY-MM-DD'. */
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const buildDueAt = (d: string, t: string): Date | undefined => {
  const m = d.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return undefined;
  let hours = 0;
  let mins = 0;
  const tm = t.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);
  if (tm) {
    // ehr#386: "14:00" (no AM / PM) is 14:00; it was read as 2:00 AM
    hours = tm[3] ? Number(tm[1]) % 12 : Number(tm[1]);
    mins = Number(tm[2]);
    if (tm[3] && /PM/i.test(tm[3])) hours += 12;
  }
  const dt = new Date(Number(m[3]), Number(m[1]) - 1, Number(m[2]), hours, mins);
  return Number.isNaN(dt.getTime()) ? undefined : dt;
};

/** Custom dropdown (dot + label + chevron) — mirrors the mockup's `.dd` widget. */
/** Searchable contact select — mirrors the mockup's `.searchable-select`. */
function ContactSelect({
  contacts,
  value,
  onChange,
}: {
  contacts: LinkContact[];
  value: string | null;
  onChange: (id: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  const label = (c: LinkContact) => `${c.firstName} ${c.lastName}`.trim();
  const catLabel = (c: string) => c.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
  const sel = contacts.find((c) => c.id === value);
  const filtered = contacts.filter((c) => label(c).toLowerCase().includes(q.toLowerCase()));
  return (
    <div className={`searchable-select${open ? ' open' : ''}`} ref={ref}>
      <button
        type="button"
        className={`searchable-select-input${sel ? '' : ' empty'}`}
        onClick={() => setOpen((v) => !v)}
      >
        {sel ? label(sel) : '— Not linked —'}
      </button>
      <div className="searchable-select-panel" role="listbox">
        <input
          className="searchable-select-search"
          placeholder="Search contacts…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <div className="searchable-select-options">
          <div
            className="searchable-select-option"
            onClick={() => {
              onChange(null);
              setOpen(false);
            }}
          >
            <span style={{ color: 'var(--text-muted-2)' }}>— Not linked —</span>
          </div>
          {filtered.map((c) => (
            <div
              key={c.id}
              className={`searchable-select-option${c.id === value ? ' selected' : ''}`}
              onClick={() => {
                onChange(c.id);
                setOpen(false);
              }}
            >
              <span>{label(c)}</span>
              <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>
                {catLabel(c.category)}
              </span>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="searchable-select-empty">No contacts found</div>
          )}
        </div>
      </div>
    </div>
  );
}

interface DrawerTask {
  title: string;
  priority: 'urgent' | 'high' | 'normal' | 'low';
  status: TaskStatus;
  dueAt: string | null;
  doneAt: string | null;
  body: string | null;
  contactId: string | null;
  createdById: string;
  createdAt: string | null;
  assignees: { userId: string }[];
  sharedWith?: string[];
  checklist?: { id: string; title: string; done: boolean }[];
  recurrence?: Recurrence;
  reminders?: { id: string; label: string }[];
}
interface TaskNote {
  id: string;
  type: string;
  body: string;
  authorId: string;
  createdAt: string;
  sharedWith: string[];
  sharedAccess: Record<string, NoteAccess>;
  /** ehr#736: set on an archived note (hidden until Show archived). */
  archivedAt?: string | null;
}
// Curated task-form library (matches the CRM's intake set). Fixed, not the org's
// clinical forms — attachments persist by these keys.
const TASK_FORMS: { key: string; name: string; blurb: string; icon: string }[] = [
  {
    key: 'presenting_problem',
    name: 'Presenting Problem',
    blurb: "Understand why they're reaching out and what they're experiencing.",
    icon: '<path d="M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2V9M9 21H5a2 2 0 0 1-2-2V9m0 0h18"/>',
  },
  {
    key: 'safety_risk',
    name: 'Safety & Risk',
    blurb:
      'Standard screening to confirm the appropriate level of care. Ask directly and without judgment.',
    icon: '<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>',
  },
  {
    key: 'insurance_billing',
    name: 'Insurance & Billing',
    blurb: 'Confirm coverage or set up self-pay before scheduling.',
    icon: '<rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/>',
  },
  {
    key: 'logistics',
    name: 'Logistics',
    blurb: 'Confirm location, telehealth readiness, and how to reach them.',
    icon: '<circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>',
  },
];
type FillQuestion = { id: string; label: string; type: 'text' | 'textarea' | 'yesno' };
const TASK_FORM_QUESTIONS: Record<string, FillQuestion[]> = {
  presenting_problem: [
    {
      id: 'pp_reason',
      label: 'What brings you in now — why are you seeking help at this time?',
      type: 'textarea',
    },
    {
      id: 'pp_symptoms',
      label: "What are the main symptoms or concerns you're experiencing?",
      type: 'textarea',
    },
    {
      id: 'pp_onset',
      label: 'When did these concerns begin, and how have they changed over time?',
      type: 'textarea',
    },
    {
      id: 'pp_impact',
      label: 'How are these concerns affecting daily life (work, relationships, sleep, etc.)?',
      type: 'textarea',
    },
    {
      id: 'pp_prior',
      label: "Have you had mental health care before? If so, what helped or didn't?",
      type: 'textarea',
    },
  ],
  safety_risk: [
    {
      id: 'sr_si',
      label:
        'Currently or in the past, have you had thoughts of suicide or of being better off dead?',
      type: 'yesno',
    },
    {
      id: 'sr_selfharm',
      label: 'Have you ever intentionally hurt yourself or engaged in self-harm?',
      type: 'yesno',
    },
    { id: 'sr_harm', label: 'Have you had thoughts or urges to harm someone else?', type: 'yesno' },
    {
      id: 'sr_intent',
      label: 'Do you currently have any intent or means to act on these thoughts?',
      type: 'yesno',
    },
    {
      id: 'sr_support',
      label: 'Do you have people or supports you can turn to when things feel unsafe?',
      type: 'yesno',
    },
  ],
  insurance_billing: [
    { id: 'ib_provider', label: 'Who is your insurance provider (carrier)?', type: 'text' },
    {
      id: 'ib_plan',
      label: 'What is your plan type (e.g., PPO, HMO, EPO, Medicaid)?',
      type: 'text',
    },
    { id: 'ib_member', label: 'What is the member ID / policy number on the card?', type: 'text' },
    {
      id: 'ib_benefits',
      label: 'Do you know your copay, deductible, or behavioral-health coverage details?',
      type: 'textarea',
    },
    {
      id: 'ib_selfpay',
      label: 'If self-pay, what payment method and arrangement do you prefer?',
      type: 'textarea',
    },
  ],
  logistics: [
    {
      id: 'lg_state',
      label:
        "Which state are you physically located in for appointments? (Must match where we're licensed.)",
      type: 'text',
    },
    { id: 'lg_modality', label: 'Will sessions be in-person or via telehealth?', type: 'text' },
    {
      id: 'lg_tech',
      label:
        'For telehealth: do you have a private space, a camera-enabled device, and reliable internet?',
      type: 'textarea',
    },
    {
      id: 'lg_timezone',
      label: 'What time zone are you in, and what days/times generally work best?',
      type: 'textarea',
    },
    {
      id: 'lg_emergency',
      label: 'Who is an emergency contact we can reach if needed (name, relationship, phone)?',
      type: 'textarea',
    },
  ],
};

/** Task form fill modal — mirrors the mockup's `.task-form-fill-modal`. */
function TaskFormFillModal({
  form,
  onClose,
  onSave,
}: {
  form: { key: string; name: string; blurb: string; icon: string };
  onClose: () => void;
  onSave: (answers: Record<string, string>) => void;
}) {
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const questions = TASK_FORM_QUESTIONS[form.key] ?? [];
  const set = (id: string, v: string) => setAnswers((a) => ({ ...a, [id]: v }));
  return (
    <div className="task-form-fill-modal" onClick={onClose}>
      <div className="task-form-fill-panel" onClick={(e) => e.stopPropagation()}>
        <div className="task-form-fill-head">
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 38,
              height: 38,
              background: 'var(--mint-pale)',
              borderRadius: 10,
              border: '1px solid var(--mint-pale-2)',
            }}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#1a8a66"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
              dangerouslySetInnerHTML={{ __html: form.icon }}
            />
          </span>
          <div style={{ flex: 1 }}>
            <h3>{form.name}</h3>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '2px 0 0' }}>
              {form.blurb}
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: 22,
              color: 'var(--text-muted-2)',
              cursor: 'pointer',
              padding: '4px 8px',
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>
        <div className="task-form-fill-body">
          {questions.map((q) => (
            <div className="task-form-fill-q" key={q.id}>
              <label>{q.label}</label>
              {q.type === 'textarea' ? (
                <textarea
                  rows={3}
                  value={answers[q.id] ?? ''}
                  onChange={(e) => set(q.id, e.target.value)}
                />
              ) : q.type === 'text' ? (
                <input
                  type="text"
                  value={answers[q.id] ?? ''}
                  onChange={(e) => set(q.id, e.target.value)}
                />
              ) : (
                <div className="task-form-fill-yesno">
                  <button
                    type="button"
                    className={answers[q.id] === 'yes' ? 'active-yes' : ''}
                    onClick={() => set(q.id, 'yes')}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    className={answers[q.id] === 'no' ? 'active-no' : ''}
                    onClick={() => set(q.id, 'no')}
                  >
                    No
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="task-form-fill-foot">
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" onClick={() => onSave(answers)}>
            Save &amp; Close
          </button>
        </div>
      </div>
    </div>
  );
}
const NOTE_PILLS: { value: string; label: string }[] = [
  { value: 'note', label: 'Note' },
  { value: 'call', label: 'Call' },
  { value: 'email', label: 'Email' },
  { value: 'outreach', label: 'Text' },
  { value: 'meeting', label: 'Meeting' },
  { value: 'other', label: 'Other' },
];

// #22: task recurrence uses the shared RecurrenceField (the same Google-Calendar
// style picker as the Follow-up / New Appt window), so "Custom…" works everywhere.
const REMINDER_PRESETS: { id: string; label: string }[] = [
  { id: 'at', label: 'At due time' },
  { id: '5m', label: '5 minutes before' },
  { id: '15m', label: '15 minutes before' },
  { id: '30m', label: '30 minutes before' },
  { id: '1h', label: '1 hour before' },
  { id: '2h', label: '2 hours before' },
  { id: '1d', label: '1 day before' },
  { id: '2d', label: '2 days before' },
  { id: '1w', label: '1 week before' },
];
const REMINDER_UNITS = ['minutes', 'hours', 'days', 'weeks'] as const;
type ReminderUnit = (typeof REMINDER_UNITS)[number];
const customReminderLabel = (n: number, unit: ReminderUnit): string => {
  const singular = unit.slice(0, -1); // minutes → minute
  return `${n} ${n === 1 ? singular : unit} before`;
};
const twoDigit = (n: number) => String(n).padStart(2, '0');
const relTime = (iso: string | null): string => {
  if (!iso) return 'just now';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return new Date(iso).toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
};

const NAME_ERR = 'Give the task a name first.';
const DUE_ERR = 'Pick a due date first.';
const BOTH_ERR = 'Give the task a name and a due date first.';
const MDY_RE = /^\d{2}\/\d{2}\/\d{4}$/;

/**
 * ehr#736 (Jay 10-31): a saved task is archived, never deleted. DELETE only discards an unsaved draft
 * (+ New Task's row, closed without saving); if the task was saved after all (405 ARCHIVE_ONLY) it
 * is archived instead, so nothing anyone saved is lost.
 */
export function discardTask(id: string): Promise<unknown> {
  return api
    .del(`/tasks/${id}`)
    .catch((e: unknown) =>
      e instanceof ApiError && e.status === 405
        ? archiveItem({ kind: 'task', id })
        : Promise.reject(e),
    );
}

export interface ArchivedTask {
  id: string;
  title: string;
  dueAt: string | null;
  archivedAt: string | null;
  createdById?: string;
  assignees?: { userId: string }[];
  sharedWith?: string[];
}

/**
 * ehr#736: the "Show archived" switch under a task list and, when it's on, the archived tasks with
 * Restore. `query` narrows the list as the page's own list is narrowed (e.g. `contact=<id>`).
 */
export function ArchivedTasks({
  query = '',
  onOpen,
  onRestored,
  canRestore,
}: {
  query?: string;
  onOpen?: (id: string) => void;
  onRestored: () => void;
  /** ehr#730: which archived tasks show Restore (a client's Tasks tab at View: the person's own). */
  canRestore?: (t: ArchivedTask) => boolean;
}) {
  const mayRestore = (t: ArchivedTask) => !canRestore || canRestore(t);
  const [on, setOn] = useState(false);
  const [rows, setRows] = useState<ArchivedTask[] | null>(null);
  const [busy, setBusy] = useState(false);
  const load = () =>
    api
      .get<ArchivedTask[]>(archivedUrl(`/tasks${query ? `?${query}` : ''}`))
      .then(setRows)
      .catch(() => setRows([]));
  useEffect(() => {
    if (on) void load();
    else setRows(null);
  }, [on, query]);
  const restore = (id: string) => {
    setBusy(true);
    restoreItem({ kind: 'task', id })
      .then(() => {
        void load();
        onRestored();
      })
      .catch((e: unknown) => showError(errorText(e, 'Could not restore this task.')))
      .finally(() => setBusy(false));
  };
  return (
    <div className="archived-tasks">
      <div className="archived-bar">
        <ShowArchivedToggle on={on} onChange={setOn} count={rows?.length} />
      </div>
      {on && rows && (
        <div className="archived-list" aria-label="Archived tasks">
          {rows.length === 0 ? (
            <p className="muted">No archived tasks.</p>
          ) : (
            rows.map((t) => (
              <div key={t.id} className="archived-task-row">
                <button
                  type="button"
                  className="link-btn archived-task-title"
                  onClick={() => onOpen?.(t.id)}
                  disabled={!onOpen}
                >
                  {t.title || 'Untitled task'}
                </button>
                <span className="archived-tag">Archived</span>
                {t.dueAt && (
                  <span className="muted archived-task-due">
                    Due {new Date(t.dueAt).toLocaleDateString('en-US')}
                  </span>
                )}
                {mayRestore(t) && <RestoreButton onClick={() => restore(t.id)} disabled={busy} />}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export function TaskDrawer({
  editId,
  fresh = false,
  onClose,
  onSaved,
  viewOnlyTab = false,
}: {
  editId: string | null;
  /** #338: opened on a task just created (create-on-open), not an existing one. */
  fresh?: boolean;
  onClose: () => void;
  onSaved: () => void;
  /** ehr#730: opened from a client's Tasks tab shared at View (Share & Access). Someone else's task
   *  shows read-only; the task's own people (creator, assignees, shared with) still change it. */
  viewOnlyTab?: boolean;
}) {
  const { user } = useSession();
  // ehr#411: the practice's status names and colours (Settings → Task Statuses), in its order.
  const statusOpts = useTaskStatuses().map((r) => ({ value: r.key, label: r.label, dot: r.color }));
  const isEdit = Boolean(editId);
  const [users, setUsers] = useState<OrgUser[]>([]);
  const [contacts, setContacts] = useState<LinkContact[]>([]);
  const [title, setTitle] = useState('');
  const [status, setStatus] = useState('open');
  const [priority, setPriority] = useState('normal');
  const [assignees, setAssignees] = useState<string[]>([]);
  const [dueDate, setDueDate] = useState('');
  const [dueTime, setDueTime] = useState('');
  const [contactId, setContactId] = useState<string | null>(null);
  const [description, setDescription] = useState('');
  // ehr#487 / #488 / #489: recurrence, reminders and the checklist save with the task.
  const [recurrence, setRecurrence] = useState<Recurrence>(NO_RECURRENCE);
  const [reminders, setReminders] = useState<{ id: string; label: string }[]>([]);
  const [checklist, setChecklist] = useState<{ id: string; title: string; done: boolean }[]>([]);
  const [checkInput, setCheckInput] = useState('');
  const [reminderPickerOpen, setReminderPickerOpen] = useState(false);
  const [remCustomVal, setRemCustomVal] = useState('30');
  const [remCustomUnit, setRemCustomUnit] = useState<ReminderUnit>('minutes');
  const reminderPickerRef = useRef<HTMLDivElement>(null);
  const [sharedWith, setSharedWith] = useState<{ userId: string; access: 'view' | 'edit' }[]>([]);
  const [shareLevel, setShareLevel] = useState<'view' | 'edit'>('view');
  const [sharePickerOpen, setSharePickerOpen] = useState(false);
  const sharePickerRef = useRef<HTMLDivElement>(null);
  const [noteType, setNoteType] = useState('note');
  const [noteText, setNoteText] = useState('');
  const [notes, setNotes] = useState<TaskNote[]>([]);
  const [noteBusy, setNoteBusy] = useState(false);
  // #24 — task-note sharing, ported 1:1 from the contact Notes tab (V38).
  const [nsPickerOpen, setNsPickerOpen] = useState(false); // composer "share with" picker
  const [nsShareIds, setNsShareIds] = useState<Set<string>>(new Set()); // composer selection
  const [nsAccess, setNsAccess] = useState<NoteAccess>('edit'); // composer access level
  const [nsSearch, setNsSearch] = useState('');
  const [noteShareId, setNoteShareId] = useState<string | null>(null); // per-note popover
  const [noteEditId, setNoteEditId] = useState<string | null>(null);
  const [noteEditDraft, setNoteEditDraft] = useState('');
  const [noteConfirmDelId, setNoteConfirmDelId] = useState<string | null>(null);
  const [notePendingUnshare, setNotePendingUnshare] = useState<{
    noteId: string;
    userId: string;
    name: string;
  } | null>(null);
  const [notePendingCancelAll, setNotePendingCancelAll] = useState<{
    noteId: string;
    names: string;
  } | null>(null);
  const [formsOpen, setFormsOpen] = useState(false);
  const [attachedFormIds, setAttachedFormIds] = useState<string[]>([]);
  const [formSearch, setFormSearch] = useState('');
  const [fillForm, setFillForm] = useState<(typeof TASK_FORMS)[number] | null>(null);
  const [maximized, setMaximized] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [askDiscard, setAskDiscard] = useState(false);
  const [creatorId, setCreatorId] = useState<string | null>(null);
  const [createdAt, setCreatedAt] = useState<string | null>(null);
  // ehr#730: the server refuses the same changes (tasks.route taskContact)
  const readOnly =
    viewOnlyTab &&
    !ownTask(
      {
        createdById: creatorId,
        assignees: assignees.map((userId) => ({ userId })),
        sharedWith: sharedWith.map((s) => s.userId),
      },
      user?.id,
    );
  const pickerRef = useRef<HTMLDivElement>(null);
  // ehr#351: what the user has changed since the drawer opened. On a slow connection the task's own
  // load lands after they've started typing; it fills only what they haven't touched (it wiped a
  // new task's name, which then got the task deleted on Save).
  const edited = useRef(new Set<string>());
  const touch = (k: string) => edited.current.add(k);

  useEffect(() => {
    // ehr#623: assignee and creator names for clinicians too (they can't read /settings/users).
    void fetchNoteUsers().then(setUsers);
    api
      .get<{ items: LinkContact[] }>('/contacts?category=client&limit=200')
      .then((r) => setContacts(r.items ?? []))
      .catch(() => setContacts([]));
  }, []);
  useEffect(() => {
    if (!editId) return;
    api
      .get<DrawerTask>(`/tasks/${editId}`)
      .then((t) => {
        const mine = (k: string) => edited.current.has(k);
        if (!mine('title')) setTitle(t.title);
        if (!mine('priority')) setPriority(t.priority);
        if (!mine('status')) setStatus(t.status);
        if (!mine('description')) setDescription(t.body ?? '');
        if (!mine('contact')) setContactId(t.contactId);
        // ehr#487 / #488 / #489: what's saved, recorded first so loading it isn't a change
        saved.current = {
          checklist: JSON.stringify(t.checklist ?? []),
          recurrence: JSON.stringify(t.recurrence ?? null),
          reminders: JSON.stringify(t.reminders ?? []),
        };
        setRecurrence(t.recurrence ?? NO_RECURRENCE);
        setChecklist(t.checklist ?? []);
        setCheckInput('');
        setReminders(t.reminders ?? []);
        if (!mine('sharedWith'))
          setSharedWith(
            (t.sharedWith ?? []).map((userId) => ({ userId, access: 'view' as const })),
          );
        if (!mine('assignees')) setAssignees((t.assignees ?? []).map((a) => a.userId));
        setCreatorId(t.createdById);
        setCreatedAt(t.createdAt ?? null);
        if (t.dueAt && !mine('due')) {
          const d = new Date(t.dueAt);
          setDueDate(`${twoDigit(d.getMonth() + 1)}/${twoDigit(d.getDate())}/${d.getFullYear()}`);
          // ehr#489: 'HH:MM' for the Credify time picker (it shows the person's 12/24-hour format)
          if (d.getHours() || d.getMinutes())
            setDueTime(`${twoDigit(d.getHours())}:${twoDigit(d.getMinutes())}`);
        }
      })
      .catch(() => setError('Could not load this task'));
  }, [editId]);
  useEffect(() => {
    if (!pickerOpen) return;
    const h = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) setPickerOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [pickerOpen]);

  // ehr#487 / #488 / #489: the checklist, repeat rule and reminders save as they change (edit mode),
  // like the other fields; `saved` is what the server has, so the load itself saves nothing.
  const saved = useRef<{ checklist: string; recurrence: string; reminders: string } | null>(null);
  useEffect(() => {
    if (!editId || !saved.current) return;
    const now = {
      checklist: JSON.stringify(checklist),
      recurrence: JSON.stringify(recurrence),
      reminders: JSON.stringify(reminders),
    };
    const changed = (Object.keys(now) as (keyof typeof now)[]).filter(
      (k) => now[k] !== saved.current![k],
    );
    if (!changed.length) return;
    saved.current = now;
    api
      .patch(`/tasks/${editId}`, {
        ...(changed.includes('checklist') ? { checklist } : {}),
        ...(changed.includes('recurrence') ? { recurrence } : {}),
        ...(changed.includes('reminders') ? { reminders } : {}),
      })
      .catch(() => setError('Could not save change'));
  }, [editId, checklist, recurrence, reminders]);

  // Edit mode autosaves each field via PATCH; new mode collects and POSTs on Create.
  const patch = (data: Record<string, unknown>) => {
    if (!editId || readOnly) return;
    api.patch(`/tasks/${editId}`, data).catch(() => setError('Could not save change'));
  };
  // ehr#411: answers when the save has landed, so Complete Task reloads the list after it, not before
  const saveStatus = (v: string): Promise<void> => {
    if (readOnly) return Promise.resolve();
    touch('status');
    setStatus(v);
    if (!editId) return Promise.resolve();
    return api
      .patch(`/tasks/${editId}`, { status: v })
      .then(() => undefined)
      .catch(() => setError('Could not update status'));
  };
  const savePriority = (v: string) => {
    touch('priority');
    setPriority(v);
    patch({ priority: v });
  };
  const saveAssignees = (next: string[]) => {
    touch('assignees');
    setAssignees(next);
    patch({ assigneeIds: next });
  };
  // ehr#489: the Credify time picker hands back 'HH:MM' (or '' when cleared) once a time is picked
  // or typed. The free-text box it replaced read "3pm" as no time (midnight), so a reminder fired at
  // the wrong time. The clock sets hour, minute and AM / PM one click at a time, so the save waits
  // for the last click (a reminder shouldn't fire for the 3:00 AM on the way to 3:30 PM); closing
  // the task saves it straight away, and a due-date save (which has the new time) replaces it.
  const dueTimeSave = useRef<{ timer: number; run: () => void } | null>(null);
  const saveDue = (dd: string = dueDate, tt: string = dueTime) => {
    if (dueTimeSave.current) window.clearTimeout(dueTimeSave.current.timer);
    dueTimeSave.current = null;
    patch({ dueAt: buildDueAt(dd, tt)?.toISOString() ?? null });
  };
  const changeDueTime = (v: string) => {
    touch('due');
    setDueTime(v);
    if (dueTimeSave.current) window.clearTimeout(dueTimeSave.current.timer);
    const run = () => saveDue(dueDate, v);
    dueTimeSave.current = { run, timer: window.setTimeout(run, 800) };
  };
  useEffect(
    () => () => {
      // closing the window saves a time still waiting
      dueTimeSave.current?.run();
    },
    [],
  );
  // ehr#488: a repeat needs a date to repeat from: picking one on a task with no due date makes
  // today the due date (the pills already read from today).
  const changeRecurrence = (r: Recurrence) => {
    if (r && !MDY_RE.test(dueDate)) {
      const mdy = isoToMdy(todayISO());
      touch('due');
      setDueDate(mdy);
      saveDue(mdy);
      if (error === DUE_ERR) setError('');
      if (error === BOTH_ERR) setError(NAME_ERR);
    }
    setRecurrence(r);
  };
  const saveContact = (id: string | null) => {
    touch('contact');
    setContactId(id);
    if (editId) patch({ contactId: id });
  };
  const loadNotes = () => {
    if (!editId) return;
    api
      .get<TaskNote[]>(`/tasks/${editId}/notes`)
      .then(setNotes)
      .catch(() => setNotes([]));
  };
  useEffect(loadNotes, [editId]);
  // ehr#736: archived notes stay on the task, hidden until Show archived
  const liveNotes = notes.filter((n) => !n.archivedAt);
  const archivedNotes = notes.filter((n) => n.archivedAt);
  const [showArchivedNotes, setShowArchivedNotes] = useState(false);
  // Candidate recipients for a task note: org teammates, minus me (#24).
  const noteCandidates = users.filter((u) => u.id !== user?.id);
  const canEditNote = (n: TaskNote) =>
    Boolean(
      user &&
      (n.authorId === user.id || canManage(user.role) || n.sharedAccess[user.id] === 'edit'),
    );
  const firstNameOf = (id: string) => {
    const u = users.find((x) => x.id === id);
    return u ? uName(u).split(' ')[0] : '';
  };
  // Create the note, then optionally share it at the chosen access level.
  const createNote = (shares: NoteShare[]) => {
    if (!editId || !noteText.trim()) return;
    setNoteBusy(true);
    api
      .post<TaskNote>(`/tasks/${editId}/notes`, { type: noteType, body: noteText.trim() })
      .then(async (created) => {
        if (shares.length && created?.id) {
          await api
            .post(`/tasks/${editId}/notes/${created.id}/share`, { shares })
            .catch(() => undefined);
        }
        setNoteText('');
        setNsPickerOpen(false);
        setNsShareIds(new Set());
        setNsSearch('');
        loadNotes();
      })
      .catch(() => setError('Could not add note'))
      .finally(() => setNoteBusy(false));
  };
  const addNote = () => createNote([]);
  const saveAndShareNote = () =>
    createNote([...nsShareIds].map((userId) => ({ userId, access: nsAccess })));
  const saveEditNote = (id: string) => {
    if (!editId || !noteEditDraft.trim()) return;
    setNoteBusy(true);
    api
      .patch(`/tasks/${editId}/notes/${id}`, { body: noteEditDraft.trim() })
      .then(() => {
        setNoteEditId(null);
        loadNotes();
      })
      .catch(() => setError('Could not save note'))
      .finally(() => setNoteBusy(false));
  };
  const doDeleteNote = () => {
    const id = noteConfirmDelId;
    if (!editId || !id) return;
    setNoteBusy(true);
    // ehr#736 (Jay 10-31): archived, never deleted
    archiveItem({ kind: 'taskNote', taskId: editId, id })
      .then(loadNotes)
      .catch(() => setError('Could not archive note'))
      .finally(() => {
        setNoteBusy(false);
        setNoteConfirmDelId(null);
      });
  };
  const restoreNote = (id: string) => {
    if (!editId) return;
    setNoteBusy(true);
    restoreItem({ kind: 'taskNote', taskId: editId, id })
      .then(loadNotes)
      .catch(() => setError('Could not restore note'))
      .finally(() => setNoteBusy(false));
  };
  // Replace a note's complete share set (V38 replace semantics).
  const postNoteShare = (noteId: string, shares: NoteShare[]) => {
    if (!editId) return;
    api
      .post(`/tasks/${editId}/notes/${noteId}/share`, { shares })
      .then(loadNotes)
      .catch(() => setError('Could not update sharing'));
  };
  const addNoteShare = (noteId: string, userId: string, access: NoteAccess) => {
    const n = notes.find((x) => x.id === noteId);
    if (!n) return;
    postNoteShare(noteId, [
      ...noteShares(n).filter((s) => s.userId !== userId),
      { userId, access },
    ]);
  };
  const confirmNoteUnshare = () => {
    if (!notePendingUnshare) return;
    const n = notes.find((x) => x.id === notePendingUnshare.noteId);
    if (n)
      postNoteShare(
        n.id,
        noteShares(n).filter((s) => s.userId !== notePendingUnshare.userId),
      );
    setNotePendingUnshare(null);
  };
  const confirmNoteCancelAll = () => {
    if (notePendingCancelAll) postNoteShare(notePendingCancelAll.noteId, []);
    setNotePendingCancelAll(null);
  };
  const loadAttachedForms = () => {
    if (!editId) return;
    api
      .get<{ formId: string }[]>(`/tasks/${editId}/forms`)
      .then((rows) => setAttachedFormIds(rows.map((r) => r.formId)))
      .catch(() => setAttachedFormIds([]));
  };
  useEffect(loadAttachedForms, [editId]);
  const attachForm = (formId: string) => {
    if (!editId) return;
    api
      .post(`/tasks/${editId}/forms`, { formId })
      .then(loadAttachedForms)
      .catch(() => setError('Could not attach form'));
  };
  const detachForm = (formId: string) => {
    if (!editId) return;
    api
      .del(`/tasks/${editId}/forms/${formId}`)
      .then(loadAttachedForms)
      .catch(() => undefined);
  };
  // Save a filled form: attach it + record the Q&A as a task note (target: note).
  const saveFormFill = (answers: Record<string, string>) => {
    if (!editId || !fillForm) return;
    const qs = TASK_FORM_QUESTIONS[fillForm.key] ?? [];
    const body =
      `${fillForm.name}\n\n` +
      qs
        .map((q) => {
          const a = answers[q.id];
          const val =
            q.type === 'yesno' ? (a === 'yes' ? 'Yes' : a === 'no' ? 'No' : '—') : a?.trim() || '—';
          return `${q.label}\n${val}`;
        })
        .join('\n\n');
    attachForm(fillForm.key);
    api
      .post(`/tasks/${editId}/notes`, { type: 'note', body })
      .then(loadNotes)
      .catch(() => setError('Could not save form'));
    setFillForm(null);
  };
  useEffect(() => {
    if (!reminderPickerOpen) return;
    const h = (e: MouseEvent) => {
      if (reminderPickerRef.current && !reminderPickerRef.current.contains(e.target as Node))
        setReminderPickerOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [reminderPickerOpen]);

  useEffect(() => {
    if (!sharePickerOpen) return;
    const h = (e: MouseEvent) => {
      if (sharePickerRef.current && !sharePickerRef.current.contains(e.target as Node))
        setSharePickerOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [sharePickerOpen]);

  // Create-on-open leaves a fresh row; discard it on close if nothing was entered. #338: a task
  // needs a name, so with anything else entered Save / close asks for one instead of keeping an
  // untitled task. The client a fresh task was opened from doesn't count as entered.
  const titleRef = useRef<HTMLInputElement>(null);
  const dueRef = useRef<HTMLDivElement>(null);
  /** #352: what's still missing (a name; a due date when `needDue`), said and marked; false if none. */
  const askForMissing = (needDue: boolean): boolean => {
    const noName = !title.trim();
    const noDue = needDue && !MDY_RE.test(dueDate);
    if (!noName && !noDue) return false;
    setError(noName && noDue ? BOTH_ERR : noName ? NAME_ERR : DUE_ERR);
    if (noName) titleRef.current?.focus();
    else dueRef.current?.querySelector('input')?.focus();
    return true;
  };
  // ehr#338 / #344: a new task is a hidden draft until it's saved, so nobody's list, Dashboard or
  // overdue badge shows it while it's being filled in, or after it's left without saving. Save
  // (or closing it with its name and due date, as before) publishes it.
  const publish = async (): Promise<boolean> => {
    if (!fresh || !editId) return true;
    try {
      await api.patch(`/tasks/${editId}`, { draft: false });
      return true;
    } catch {
      setError('Could not save the task. Try again.');
      return false;
    }
  };
  // `saving`: the Save button, not × or a click outside.
  const finishClose = (saving = false) => {
    if (readOnly) {
      onClose(); // ehr#730: nothing here can change
      return;
    }
    dueTimeSave.current?.run(); // ehr#489: a picked time still waiting saves first
    if (editId && !title.trim()) {
      // #352 (10-11): on a new task anything changed counts, status and priority too (they were
      // dropped on Save without a word); an older untitled task keeps the earlier rule.
      const nothingEntered = fresh
        ? edited.current.size === 0 && liveNotes.length === 0 && attachedFormIds.length === 0
        : assignees.length === 0 &&
          !contactId &&
          !description.trim() &&
          !dueDate &&
          liveNotes.length === 0 &&
          attachedFormIds.length === 0;
      if (nothingEntered) {
        void discardTask(editId).finally(onSaved);
        return;
      }
    }
    // #352: a task needs a name, and a due date: Save needs one, and so does closing a new task;
    // an older task saved without one can still be closed.
    if (editId && askForMissing(saving || fresh)) {
      // ehr#490: ×, Escape or a click outside on a new task that can't be saved yet (no name or due
      // date) asks whether to discard it, instead of leaving the panel stuck open.
      if (fresh && !saving) setAskDiscard(true);
      return;
    }
    void publish().then((ok) => ok && onSaved());
  };

  // ehr#736 (Jay 10-31): Archive Task (never Delete). A new task that was never saved is discarded.
  const del = () => {
    if (!editId) return;
    setBusy(true);
    (fresh ? discardTask(editId) : archiveItem({ kind: 'task', id: editId }))
      .then(onSaved)
      .catch((e: unknown) => {
        setError(errorText(e, fresh ? 'Could not discard task' : 'Could not archive task'));
        setBusy(false);
      });
  };

  // Complete Task footer button — mark done, then save + close. #352: only a task with a name (and,
  // when new, a due date): it skipped every check and kept a nameless, dateless task.
  const completeTask = () => {
    dueTimeSave.current?.run(); // ehr#489
    if (editId && askForMissing(fresh)) return;
    void publish().then((ok) => {
      if (!ok) return;
      // ehr#411: the card reloaded before the status save landed, and could still read Open
      void saveStatus('done').then(onSaved);
    });
  };

  const addChecklistItem = () => {
    const title = checkInput.trim();
    if (!title) return;
    setChecklist((c) => [...c, { id: Math.random().toString(36).slice(2), title, done: false }]);
    setCheckInput('');
  };

  const creator = users.find((u) => u.id === creatorId);
  const creatorName = isEdit
    ? creator
      ? uName(creator)
      : '—'
    : user
      ? displayName(user.name, user.email)
      : 'You';
  const creatorColor = avatarColor((isEdit ? creatorId : user?.id) ?? 'me');
  const creatorInitials = creator ? uInitials(creator) : creatorName.slice(0, 2).toUpperCase();
  const assignedUsers = users.filter((u) => assignees.includes(u.id));
  const unassigned = users.filter((u) => !assignees.includes(u.id));
  const hasDue = MDY_RE.test(dueDate);
  const dueMissing = (error === DUE_ERR || error === BOTH_ERR) && !hasDue;
  const nameMissing = (error === NAME_ERR || error === BOTH_ERR) && !title.trim();
  const linkedContact = contacts.find((c) => c.id === contactId);

  const create = () => {
    if (!title.trim()) return;
    if (!hasDue) {
      setError(DUE_ERR);
      dueRef.current?.querySelector('input')?.focus();
      return;
    }
    setBusy(true);
    setError('');
    const dueAt = buildDueAt(dueDate, dueTime);
    api
      .post<{ id: string }>('/tasks', {
        title: title.trim(),
        priority,
        body: description.trim() || undefined,
        dueAt: dueAt ? dueAt.toISOString() : undefined,
        contactId: contactId || undefined,
        assigneeIds: assignees.length ? assignees : undefined,
        sharedWith: sharedWith.length ? sharedWith.map((s) => s.userId) : undefined,
        checklist,
        recurrence,
        reminders,
      })
      .then(async (t) => {
        if (status === 'done' && t?.id)
          await api.post(`/tasks/${t.id}/done`).catch(() => undefined);
        onSaved();
      })
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : 'Could not create task'))
      .finally(() => setBusy(false));
  };

  return (
    <>
      <div className="modal-backdrop" onClick={() => finishClose()}>
        <div
          className={maximized ? 'drawer maximized' : 'drawer'}
          role="dialog"
          aria-modal="true"
          aria-label={isEdit ? 'Edit task' : 'New task'}
          onClick={(e) => e.stopPropagation()}
        >
          {/* ehr#585 (Chase 10-21, HANDOFF A): every field kept, less vertical space (styles.css) */}
          <header className="task-drawer-head">
            <div className="task-drawer-head-top">
              <button
                type="button"
                className={`task-status-toggle${status === 'done' ? ' done' : ''}`}
                disabled={readOnly}
                title={status === 'done' ? 'Reopen' : 'Mark done'}
                onClick={() => void saveStatus(status === 'done' ? 'open' : 'done')}
              />
              <div className="task-drawer-title">
                <input
                  ref={titleRef}
                  autoFocus
                  value={title}
                  readOnly={readOnly}
                  placeholder="Enter new task name…"
                  aria-invalid={nameMissing}
                  onChange={(e) => {
                    touch('title');
                    setTitle(e.target.value);
                    if (e.target.value.trim() && error === NAME_ERR) setError('');
                    if (e.target.value.trim() && error === BOTH_ERR) setError(DUE_ERR);
                  }}
                  onBlur={() => {
                    if (editId && title.trim()) patch({ title: title.trim() });
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                  }}
                />
              </div>
              <div className="drawer-head-actions">
                <button
                  className="drawer-maximize"
                  onClick={() => setMaximized((m) => !m)}
                  aria-label={maximized ? 'Minimize panel' : 'Maximize panel'}
                  title={maximized ? 'Minimize panel' : 'Maximize panel'}
                >
                  {maximized ? (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="4 14 10 14 10 20" />
                      <polyline points="20 10 14 10 14 4" />
                      <line x1="14" y1="10" x2="21" y2="3" />
                      <line x1="3" y1="21" x2="10" y2="14" />
                    </svg>
                  ) : (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="15 3 21 3 21 9" />
                      <polyline points="9 21 3 21 3 15" />
                      <line x1="21" y1="3" x2="14" y2="10" />
                      <line x1="3" y1="21" x2="10" y2="14" />
                    </svg>
                  )}
                </button>
                <button className="drawer-close" onClick={() => finishClose()} aria-label="Close">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="task-drawer-meta">
              {status === 'done' ? (
                <span
                  className="task-field-value-pill"
                  style={{
                    background: 'var(--mint-pale)',
                    color: 'var(--green-deep)',
                    borderColor: 'var(--mint-pale-2)',
                  }}
                >
                  Done
                </span>
              ) : (
                <span
                  className="task-field-value-pill"
                  style={{ background: '#ecfeff', color: '#0e7490', borderColor: '#0e749033' }}
                >
                  Open
                </span>
              )}
              Created by {creatorName} · {isEdit ? relTime(createdAt) : 'just now'}
            </div>
          </header>

          <div className="task-drawer-body" inert={readOnly}>
            <div className="task-field-grid">
              <div className="task-field">
                <label>Status</label>
                <Dd value={status} options={statusOpts} onChange={saveStatus} searchable={false} />
              </div>
              <div className="task-field">
                <label>Priority</label>
                <Dd
                  value={priority}
                  options={PRIORITY_OPTS}
                  onChange={savePriority}
                  searchable={false}
                />
              </div>
              <div className="task-field task-assignees-field">
                <label>
                  Assignees
                  {assignedUsers.length > 0 && (
                    <span className="task-assignees-count">{assignedUsers.length}</span>
                  )}
                </label>
                <div className="task-assignees-list">
                  {assignedUsers.map((u) => (
                    <span className="task-assignee-chip" key={u.id}>
                      <span
                        className="task-assignee-avatar"
                        style={{ background: avatarColor(u.id) }}
                      >
                        {uInitials(u)}
                      </span>
                      <span>{uName(u)}</span>
                      <button
                        type="button"
                        className="task-assignee-chip-x"
                        aria-label="Remove"
                        onClick={() => saveAssignees(assignees.filter((x) => x !== u.id))}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  <div style={{ position: 'relative' }} ref={pickerRef}>
                    <button
                      type="button"
                      className="task-assignee-add-btn"
                      onClick={() => setPickerOpen((v) => !v)}
                    >
                      <svg
                        width="11"
                        height="11"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                      </svg>
                      Add another
                    </button>
                    {pickerOpen && (
                      <div className="dd-panel" style={{ minWidth: 200 }}>
                        {unassigned.length === 0 ? (
                          <div
                            style={{
                              padding: '8px 10px',
                              color: 'var(--text-muted-2)',
                              fontSize: 13,
                            }}
                          >
                            Everyone added
                          </div>
                        ) : (
                          unassigned.map((u) => (
                            <button
                              key={u.id}
                              type="button"
                              className="dd-opt"
                              onClick={() => {
                                saveAssignees([...assignees, u.id]);
                                setPickerOpen(false);
                              }}
                            >
                              <span
                                className="task-assignee-avatar"
                                style={{ background: avatarColor(u.id) }}
                              >
                                {uInitials(u)}
                              </span>
                              {uName(u)}
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className={`task-field${dueMissing ? ' task-field-invalid' : ''}`} ref={dueRef}>
                <label>
                  Due date <span style={{ color: 'var(--problem)' }}>*</span>
                </label>
                <DateField
                  value={mdyToISO(dueDate)}
                  onChange={(iso) => {
                    const mdy = isoToMdy(iso);
                    touch('due');
                    setDueDate(mdy);
                    saveDue(mdy);
                    if (mdy && error === DUE_ERR) setError('');
                    if (mdy && error === BOTH_ERR) setError(NAME_ERR);
                  }}
                />
              </div>
              <div className="task-field">
                <label>
                  Due time{' '}
                  <span
                    style={{
                      fontWeight: 400,
                      textTransform: 'none',
                      letterSpacing: 0,
                      color: 'var(--text-muted-2)',
                    }}
                  >
                    (optional)
                  </span>
                </label>
                {/* ehr#489: the Credify time picker (type a time or pick one), not a free-text box */}
                <div title={hasDue ? '' : 'Set a due date first'}>
                  <TimeField
                    value={dueTime}
                    onChange={changeDueTime}
                    disabled={!hasDue}
                    placeholder={hasDue ? 'Select a time' : 'Set a due date first'}
                    ariaLabel="Due time"
                  />
                </div>
              </div>
              <div className="task-field">
                <label>
                  Share{' '}
                  <span
                    style={{
                      fontWeight: 400,
                      textTransform: 'none',
                      letterSpacing: 0,
                      color: 'var(--text-muted-2)',
                    }}
                  >
                    (optional)
                  </span>
                </label>
                <div style={{ position: 'relative' }} ref={sharePickerRef}>
                  <button
                    type="button"
                    className={`task-share-field ${sharedWith.length ? 'shared' : ''}`}
                    onClick={() => setSharePickerOpen((o) => !o)}
                    title={
                      sharedWith.length
                        ? 'Change who this task is shared with'
                        : 'Share this task with a teammate'
                    }
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="18" cy="5" r="3" />
                      <circle cx="6" cy="12" r="3" />
                      <circle cx="18" cy="19" r="3" />
                      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                    </svg>
                    <span>
                      {sharedWith.length
                        ? `Shared with ${sharedWith.length} ${sharedWith.length === 1 ? 'person' : 'people'}`
                        : 'Share this task'}
                    </span>
                  </button>
                  {sharePickerOpen && (
                    <div className="task-share-pop">
                      <div className="tsp-head">
                        <strong>Share this task</strong>
                        <span>Teammates you pick can see it in their list.</span>
                      </div>
                      <div className="tsp-lvl">
                        {(['view', 'edit'] as const).map((lvl) => (
                          <button
                            key={lvl}
                            type="button"
                            className={shareLevel === lvl ? 'on' : ''}
                            onClick={() => setShareLevel(lvl)}
                          >
                            {lvl === 'view' ? 'Can view' : 'Can edit'}
                          </button>
                        ))}
                      </div>
                      <div className="tsp-list">
                        {users.filter((u) => u.id !== creatorId).length === 0 ? (
                          <div className="tsp-empty">No teammates to share with.</div>
                        ) : (
                          users
                            .filter((u) => u.id !== creatorId)
                            .map((u) => {
                              const sh = sharedWith.find((s) => s.userId === u.id);
                              return (
                                <label className="tsp-row" key={u.id}>
                                  <input
                                    type="checkbox"
                                    checked={!!sh}
                                    onChange={(e) => {
                                      const next = e.target.checked
                                        ? [...sharedWith, { userId: u.id, access: shareLevel }]
                                        : sharedWith.filter((x) => x.userId !== u.id);
                                      touch('sharedWith');
                                      setSharedWith(next);
                                      // #23 — edit mode autosaves; persist the share list too.
                                      if (editId) patch({ sharedWith: next.map((s) => s.userId) });
                                    }}
                                  />
                                  <span
                                    className="task-assignee-avatar"
                                    style={{ background: avatarColor(u.id), width: 18, height: 18 }}
                                  >
                                    {uInitials(u)}
                                  </span>
                                  <span className="tsp-name">{uName(u)}</span>
                                  {sh && (
                                    <span className="tsp-lvl-tag">
                                      {sh.access === 'edit' ? 'Edit' : 'View'}
                                    </span>
                                  )}
                                </label>
                              );
                            })
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {sharedWith.length > 0 && (
                  <div className="task-share-names">
                    {sharedWith
                      .map((s) => {
                        const u = users.find((x) => x.id === s.userId);
                        return u ? uName(u) : '?';
                      })
                      .join(', ')}
                  </div>
                )}
              </div>
              <div className="task-field" style={{ gridColumn: '1/-1' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="23 4 23 10 17 10" />
                    <polyline points="1 20 1 14 7 14" />
                    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                  </svg>
                  Recurring
                </label>
                {/* ehr#585: the field's own 6px label gap, no extra top margin */}
                <div>
                  <RecurrenceField
                    variant="pills"
                    rule={recurrence}
                    onChange={changeRecurrence}
                    anchorISO={mdyToISO(dueDate) || todayISO()}
                  />
                  {/* ehr#488: what a repeat does, so picking one visibly takes effect */}
                  {recurrence && status !== 'done' && status !== 'cancelled' && (
                    <div className="task-repeat-hint">
                      {repeatHint(mdyToISO(dueDate) || todayISO(), recurrence)}
                    </div>
                  )}
                </div>
              </div>
              <div className="task-field task-reminders-field" style={{ gridColumn: '1/-1' }}>
                <label>Reminders</label>
                {/* ehr#489 (reopen): a completed or cancelled task never sends its reminders */}
                {(status === 'done' || status === 'cancelled') && reminders.length > 0 && (
                  <div className="task-reminder-closed" role="note">
                    This task is {status === 'done' ? 'completed' : 'cancelled'}, so its reminders
                    won&apos;t be sent. Reopen it to get them.
                  </div>
                )}
                <div className="task-reminders-list">
                  {reminders.map((r) => (
                    <span key={r.id} className="task-reminder-chip">
                      <svg
                        width="10"
                        height="10"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
                        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                      </svg>
                      {r.label}
                      <button
                        type="button"
                        className="task-reminder-chip-x"
                        aria-label="Remove reminder"
                        onClick={() => setReminders((rs) => rs.filter((x) => x.id !== r.id))}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  <div style={{ position: 'relative' }} ref={reminderPickerRef}>
                    <button
                      type="button"
                      className="task-reminder-add-btn"
                      disabled={!hasDue}
                      title={hasDue ? '' : 'Set a due date first'}
                      onClick={() => setReminderPickerOpen((o) => !o)}
                    >
                      <svg
                        width="11"
                        height="11"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                      </svg>
                      Add reminder
                    </button>
                    {reminderPickerOpen && (
                      <div className="reminder-picker">
                        <div className="reminder-picker-head">Add reminder</div>
                        {REMINDER_PRESETS.map((o) => {
                          const already = reminders.some((r) => r.id === o.id);
                          return (
                            <button
                              key={o.id}
                              type="button"
                              className="reminder-picker-opt"
                              disabled={already}
                              onClick={() => {
                                setReminders((r) => [...r, { id: o.id, label: o.label }]);
                                setReminderPickerOpen(false);
                              }}
                            >
                              {o.label}
                            </button>
                          );
                        })}
                        <div className="reminder-picker-sep" />
                        <div className="reminder-picker-custom">
                          <input
                            type="number"
                            min={1}
                            value={remCustomVal}
                            onChange={(e) => setRemCustomVal(e.target.value)}
                            aria-label="Custom reminder amount"
                          />
                          <div style={{ width: 110 }}>
                            <Dd
                              value={remCustomUnit}
                              onChange={(v) => setRemCustomUnit(v as ReminderUnit)}
                              searchable={false}
                              ariaLabel="Custom reminder unit"
                              options={REMINDER_UNITS.map((u) => ({ value: u, label: u }))}
                            />
                          </div>
                          <span className="reminder-picker-custom-suffix">before</span>
                          <button
                            type="button"
                            className="btn primary reminder-picker-add"
                            onClick={() => {
                              const n = Math.max(1, Math.floor(Number(remCustomVal) || 0));
                              const id = `custom-${n}-${remCustomUnit}`;
                              const label = customReminderLabel(n, remCustomUnit);
                              setReminders((r) =>
                                r.some((x) => x.id === id) ? r : [...r, { id, label }],
                              );
                              setReminderPickerOpen(false);
                            }}
                          >
                            Add
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="task-field task-field-block">
              <label>{linkedContact ? 'Linked to contact' : 'Link to a contact'}</label>
              {linkedContact ? (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <a className="task-field-prospect-link">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="7" r="4" />
                      <path d="M5.5 21a6.5 6.5 0 0 1 13 0" />
                    </svg>
                    {`${linkedContact.firstName} ${linkedContact.lastName}`.trim()}
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      style={{ opacity: 0.7 }}
                    >
                      <polyline points="15 3 21 3 21 9" />
                      <line x1="10" y1="14" x2="21" y2="3" />
                    </svg>
                  </a>
                  <button
                    className="btn ghost-sm"
                    style={{ fontSize: 11, padding: '6px 10px', minHeight: 0 }}
                    title="Unlink"
                    onClick={() => saveContact(null)}
                  >
                    Unlink
                  </button>
                </div>
              ) : (
                <ContactSelect contacts={contacts} value={contactId} onChange={saveContact} />
              )}
            </div>

            <div className="task-field task-field-block">
              <label>Description</label>
              <textarea
                className="task-description-edit"
                placeholder="Add context, links, or notes for whoever picks this up…"
                value={description}
                onChange={(e) => {
                  touch('description');
                  setDescription(e.target.value);
                }}
                onBlur={() => {
                  if (editId) patch({ body: description.trim() || null });
                }}
              />
            </div>

            <div className="task-checklist-section">
              <div className="task-checklist-head">
                <span>Checklist</span>
                {checklist.length > 0 && (
                  <>
                    <span className="task-checklist-count">
                      {checklist.filter((i) => i.done).length}/{checklist.length}
                    </span>
                    <div className="task-checklist-progress-bar-wrap">
                      <div
                        className="task-checklist-progress-bar"
                        style={{
                          width: `${Math.round(
                            (checklist.filter((i) => i.done).length / checklist.length) * 100,
                          )}%`,
                        }}
                      />
                    </div>
                  </>
                )}
              </div>
              <div className="task-checklist-add-row">
                <input
                  type="text"
                  placeholder="Add a checklist item…"
                  value={checkInput}
                  onChange={(e) => setCheckInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addChecklistItem();
                    }
                  }}
                />
                <button type="button" className="task-checklist-add-btn" onClick={addChecklistItem}>
                  Add
                </button>
              </div>
              {checklist.map((item) => (
                <div className="task-checklist-item" key={item.id}>
                  <div className="task-checklist-item-row">
                    <input
                      type="checkbox"
                      checked={item.done}
                      onChange={() =>
                        setChecklist((c) =>
                          c.map((x) => (x.id === item.id ? { ...x, done: !x.done } : x)),
                        )
                      }
                    />
                    <span
                      className={`task-checklist-item-title${item.done ? ' done' : ''}`}
                      onClick={() =>
                        setChecklist((c) =>
                          c.map((x) => (x.id === item.id ? { ...x, done: !x.done } : x)),
                        )
                      }
                    >
                      {item.title}
                    </span>
                    <button
                      type="button"
                      className="task-checklist-item-del"
                      aria-label="Remove item"
                      onClick={() => setChecklist((c) => c.filter((x) => x.id !== item.id))}
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="task-forms-section">
              <div className="task-forms-section-head" onClick={() => setFormsOpen((v) => !v)}>
                Attached Forms
                <svg
                  className="tfs-chev"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ marginLeft: 'auto', transform: formsOpen ? 'rotate(180deg)' : 'none' }}
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </div>
              {formsOpen && (
                <div className="task-forms-body">
                  {attachedFormIds.length > 0 && (
                    <div className="task-form-chips">
                      {TASK_FORMS.filter((f) => attachedFormIds.includes(f.key)).map((f) => (
                        <span
                          className="task-form-chip"
                          key={f.key}
                          onClick={() => detachForm(f.key)}
                          title="Remove"
                        >
                          {f.name}
                          <button type="button" className="task-form-chip-x" aria-label="Remove">
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="task-form-search">
                    <input
                      type="text"
                      placeholder="Search forms…"
                      autoComplete="off"
                      value={formSearch}
                      onChange={(e) => setFormSearch(e.target.value)}
                    />
                  </div>
                  <div className="task-form-results">
                    {(() => {
                      const q = formSearch.toLowerCase();
                      const results = TASK_FORMS.filter(
                        (f) =>
                          !attachedFormIds.includes(f.key) &&
                          (f.name.toLowerCase().includes(q) || f.blurb.toLowerCase().includes(q)),
                      );
                      if (results.length === 0)
                        return (
                          <div style={{ padding: 14, color: 'var(--text-muted-2)', fontSize: 13 }}>
                            No forms found
                          </div>
                        );
                      return results.map((f) => (
                        <div
                          className="task-form-result-item"
                          key={f.key}
                          onClick={() => setFillForm(f)}
                        >
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="var(--green-primary)"
                            strokeWidth="1.75"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            style={{ flexShrink: 0 }}
                            dangerouslySetInnerHTML={{ __html: f.icon }}
                          />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600 }}>{f.name}</div>
                            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                              {f.blurb}
                            </div>
                          </div>
                          <span
                            style={{ color: 'var(--green-primary)', fontWeight: 600, fontSize: 12 }}
                          >
                            + Add
                          </span>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              )}
            </div>

            <div className="task-notes-section">
              <div className="task-notes-section-head">Task Notes</div>
              <div className="task-note-composer">
                <div className="note-type-pills">
                  {NOTE_PILLS.map((t) => (
                    <button
                      key={t.value}
                      className={`note-type-pill${noteType === t.value ? ' active' : ''}`}
                      onClick={() => setNoteType(t.value)}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
                <textarea
                  placeholder={isEdit ? 'Add a note…' : 'Save the task to add notes…'}
                  value={noteText}
                  disabled={!isEdit}
                  onChange={(e) => setNoteText(e.target.value)}
                  aria-label="New task note"
                />
                <div className="note-add-btn-row">
                  <button
                    className={`note-add-self${nsPickerOpen ? ' inactive' : ''}`}
                    disabled={!isEdit || !noteText.trim() || noteBusy}
                    onClick={() => {
                      setNsPickerOpen(false);
                      setNsShareIds(new Set());
                      addNote();
                    }}
                  >
                    {noteBusy ? 'Adding…' : 'Add for myself'}
                  </button>
                  <button
                    className={`note-add-others${nsPickerOpen ? ' active' : ''}`}
                    disabled={!isEdit || !noteText.trim() || noteBusy}
                    onClick={() => {
                      setNsPickerOpen((o) => !o);
                      setNsSearch('');
                    }}
                  >
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden
                    >
                      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                      <polyline points="16 6 12 2 8 6" />
                      <line x1="12" y1="2" x2="12" y2="15" />
                    </svg>
                    Add for myself and others
                    {nsShareIds.size > 0 && (
                      <span className="note-add-count">{nsShareIds.size}</span>
                    )}
                  </button>
                  {nsShareIds.size > 0 && (
                    <button
                      className="btn primary"
                      style={{ marginLeft: 'auto' }}
                      onClick={saveAndShareNote}
                      disabled={noteBusy}
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      Save &amp; Share
                    </button>
                  )}
                </div>
                {nsPickerOpen &&
                  (() => {
                    const nd = nsSearch.trim().toLowerCase();
                    const shown = nd
                      ? noteCandidates.filter(
                          (u) =>
                            nsUserName(u).toLowerCase().includes(nd) ||
                            (u.role ?? '').toLowerCase().includes(nd),
                        )
                      : noteCandidates;
                    return (
                      <div className="note-share-picker">
                        <div className="note-share-picker-head">
                          Share with — select one or more
                        </div>
                        {noteCandidates.length === 0 ? (
                          <div className="note-share-empty">No teammates to share with.</div>
                        ) : (
                          <>
                            <NoteAccessToggle value={nsAccess} onChange={setNsAccess} />
                            {noteCandidates.length > 1 && (
                              <div className="note-share-search-wrap">
                                <svg
                                  width="12"
                                  height="12"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  aria-hidden
                                >
                                  <circle cx="11" cy="11" r="8" />
                                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                                </svg>
                                <input
                                  className="note-share-search"
                                  type="search"
                                  placeholder="Search users…"
                                  autoComplete="off"
                                  value={nsSearch}
                                  onChange={(e) => setNsSearch(e.target.value)}
                                />
                              </div>
                            )}
                            {shown.length === 0 ? (
                              <div className="note-share-empty">No users match your search.</div>
                            ) : (
                              shown.map((u) => {
                                const nm = nsUserName(u);
                                return (
                                  <label key={u.id} className="note-share-user-row">
                                    <input
                                      type="checkbox"
                                      checked={nsShareIds.has(u.id)}
                                      onChange={() =>
                                        setNsShareIds((s) => {
                                          const nx = new Set(s);
                                          if (nx.has(u.id)) nx.delete(u.id);
                                          else nx.add(u.id);
                                          return nx;
                                        })
                                      }
                                    />
                                    <span
                                      className="user-avatar tiny"
                                      style={{ background: nsAvatarColor(u.id) }}
                                    >
                                      {nsInitials(nm)}
                                    </span>
                                    <span className="note-share-user-name">{nm}</span>
                                    <span className="note-share-user-role">
                                      {nsRoleLabel(u.role)}
                                    </span>
                                  </label>
                                );
                              })
                            )}
                          </>
                        )}
                        <div className="note-share-foot">
                          <button
                            className="btn ghost-sm"
                            onClick={() => {
                              setNsPickerOpen(false);
                              setNsSearch('');
                            }}
                          >
                            Cancel
                          </button>
                          <button
                            className="btn primary"
                            onClick={saveAndShareNote}
                            disabled={noteBusy || nsShareIds.size === 0}
                          >
                            <svg
                              width="12"
                              height="12"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              aria-hidden
                            >
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                            Save &amp; Share{nsShareIds.size > 0 ? ` (${nsShareIds.size})` : ''}
                          </button>
                        </div>
                      </div>
                    );
                  })()}
              </div>
              {liveNotes.length > 0 && (
                <div className="timeline" style={{ marginTop: 14 }}>
                  {liveNotes.map((n) => {
                    const isMyNote = n.authorId === user?.id;
                    const isSharedToMe = !isMyNote && n.sharedWith.includes(user?.id ?? '');
                    const myAccess = user ? n.sharedAccess[user.id] : undefined;
                    const pill = NOTE_PILLS.find((p) => p.value === n.type);
                    const showDelete = Boolean(isMyNote || (user && canManage(user.role)));
                    return (
                      <div
                        key={n.id}
                        className={`timeline-item ${n.type}${isSharedToMe ? ' note-from-share' : ''}`}
                      >
                        <div className="timeline-dot" />
                        <div className="timeline-meta">
                          <span className={`ttype ${n.type}`}>{pill ? pill.label : n.type}</span>
                          <span className="timeline-by">
                            <span
                              className="user-avatar tiny"
                              style={{ background: nsAvatarColor(n.authorId) }}
                            >
                              {nsInitials(firstNameOf(n.authorId) || '—')}
                            </span>
                            <strong>{firstNameOf(n.authorId) || 'Unknown'}</strong>
                          </span>
                          <span className="ttime">{relTime(n.createdAt)}</span>
                          {isMyNote && n.sharedWith.length > 0 && (
                            <span className="note-shared-badge">
                              <UploadShareIcon /> Shared with{' '}
                              {n.sharedWith.map((id) => firstNameOf(id) || 'user').join(', ')}
                            </span>
                          )}
                          {isSharedToMe && (
                            <span className="note-shared-by-badge">
                              <UploadShareIcon /> Shared by {firstNameOf(n.authorId) || 'someone'}
                              {myAccess === 'view' ? ' · view only' : ''}
                            </span>
                          )}
                          {isMyNote && (
                            <div className="share-wrap">
                              <button
                                className={`note-share-later-btn${n.sharedWith.length ? ' shared' : ''}`}
                                title={
                                  n.sharedWith.length
                                    ? `Shared with: ${n.sharedWith
                                        .map((id) => firstNameOf(id) || 'user')
                                        .join(', ')}`
                                    : 'Share note with others'
                                }
                                onClick={() => setNoteShareId((s) => (s === n.id ? null : n.id))}
                              >
                                <ShareIcon />
                                {n.sharedWith.length
                                  ? ` Shared (${n.sharedWith.length})`
                                  : ' Share note with others'}
                              </button>
                              {noteShareId === n.id && (
                                <NoteSharePopover
                                  note={n}
                                  candidates={noteCandidates}
                                  myId={user?.id}
                                  emptyText="No teammates to share with."
                                  onAdd={(uid, acc) => addNoteShare(n.id, uid, acc)}
                                  onRemove={(uid, nm) =>
                                    setNotePendingUnshare({ noteId: n.id, userId: uid, name: nm })
                                  }
                                  onCancelAll={() =>
                                    setNotePendingCancelAll({
                                      noteId: n.id,
                                      names: n.sharedWith
                                        .map((id) => firstNameOf(id) || 'user')
                                        .join(', '),
                                    })
                                  }
                                  onClose={() => setNoteShareId(null)}
                                />
                              )}
                            </div>
                          )}
                          {isMyNote && n.sharedWith.length > 0 && (
                            <button
                              className="note-cancel-share-btn"
                              onClick={() =>
                                setNotePendingCancelAll({
                                  noteId: n.id,
                                  names: n.sharedWith
                                    .map((id) => firstNameOf(id) || 'user')
                                    .join(', '),
                                })
                              }
                            >
                              <svg
                                width="11"
                                height="11"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                aria-hidden
                              >
                                <circle cx="12" cy="12" r="10" />
                                <line x1="15" y1="9" x2="9" y2="15" />
                                <line x1="9" y1="9" x2="15" y2="15" />
                              </svg>
                              Cancel Sharing
                            </button>
                          )}
                          {(canEditNote(n) || showDelete) && (
                            <div className="tbtns">
                              {canEditNote(n) && (
                                <button
                                  className="tedit"
                                  title="Edit"
                                  onClick={() => {
                                    setNoteEditId(n.id);
                                    setNoteEditDraft(n.body);
                                  }}
                                >
                                  <svg
                                    viewBox="0 0 24 24"
                                    width="12"
                                    height="12"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    aria-hidden
                                  >
                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                  </svg>
                                </button>
                              )}
                              {showDelete && (
                                <button
                                  className="tdelete tarchive"
                                  title="Archive"
                                  aria-label="Archive note"
                                  onClick={() => setNoteConfirmDelId(n.id)}
                                  disabled={noteBusy}
                                >
                                  <ArchiveIcon />
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                        {noteEditId === n.id ? (
                          <div className="tl-edit-wrap">
                            <textarea
                              value={noteEditDraft}
                              onChange={(e) => setNoteEditDraft(e.target.value)}
                              onKeyDown={(e) => {
                                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter')
                                  saveEditNote(n.id);
                                if (e.key === 'Escape') setNoteEditId(null);
                              }}
                              autoFocus
                            />
                            <div className="tl-edit-actions">
                              <span className="tl-edit-hint">⌘+Enter to save · Esc to cancel</span>
                              <button className="btn sm" onClick={() => setNoteEditId(null)}>
                                Cancel
                              </button>
                              <button
                                className="btn primary sm"
                                onClick={() => saveEditNote(n.id)}
                                disabled={noteBusy || !noteEditDraft.trim()}
                              >
                                Save
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="timeline-body">{n.body}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {archivedNotes.length > 0 && (
                <div className="archived-bar">
                  <ShowArchivedToggle
                    on={showArchivedNotes}
                    onChange={setShowArchivedNotes}
                    count={archivedNotes.length}
                  />
                </div>
              )}
              {showArchivedNotes && archivedNotes.length > 0 && (
                <div className="timeline archived-list" aria-label="Archived notes">
                  {archivedNotes.map((n) => (
                    <div key={n.id} className={`timeline-item archived ${n.type}`}>
                      <div className="timeline-dot" />
                      <div className="timeline-meta">
                        <span className="ttype archived-tag">Archived</span>
                        {Boolean(n.authorId === user?.id || (user && canManage(user.role))) && (
                          <div className="tbtns">
                            <RestoreButton onClick={() => restoreNote(n.id)} disabled={noteBusy} />
                          </div>
                        )}
                      </div>
                      <div className="timeline-body">{n.body}</div>
                    </div>
                  ))}
                </div>
              )}
              {noteConfirmDelId && (
                <ConfirmDialog
                  message="Archive this note? It leaves the list. Turn on Show archived to see it again or restore it."
                  confirmLabel="Archive"
                  danger={false}
                  busy={noteBusy}
                  onConfirm={doDeleteNote}
                  onClose={() => setNoteConfirmDelId(null)}
                />
              )}
              {notePendingUnshare && (
                <ConfirmDialog
                  message={`Stop sharing this note with ${notePendingUnshare.name}?`}
                  confirmLabel="Yes, stop sharing"
                  onConfirm={confirmNoteUnshare}
                  onClose={() => setNotePendingUnshare(null)}
                />
              )}
              {askDiscard && (
                <ConfirmDialog
                  message={`Discard this new task? It isn't saved yet: it still needs ${
                    !title.trim() && !hasDue
                      ? 'a name and a due date'
                      : !title.trim()
                        ? 'a name'
                        : 'a due date'
                  }.`}
                  confirmLabel="Discard task"
                  cancelLabel="Keep editing"
                  onConfirm={() => {
                    setAskDiscard(false);
                    if (editId) void discardTask(editId).finally(onSaved);
                    else onClose();
                  }}
                  onClose={() => setAskDiscard(false)}
                />
              )}
              {notePendingCancelAll && (
                <ConfirmDialog
                  message={`Stop sharing this note with ${notePendingCancelAll.names}?`}
                  confirmLabel="Yes, stop sharing"
                  onConfirm={confirmNoteCancelAll}
                  onClose={() => setNotePendingCancelAll(null)}
                />
              )}
            </div>

            <div className="task-comments-section">
              <label>Comments</label>
              <p
                style={{
                  fontSize: 12,
                  color: 'var(--text-muted-2)',
                  fontStyle: 'italic',
                  margin: 0,
                }}
              >
                No comments yet.
              </p>
            </div>

            <div className="task-activity-section">
              <h4>Activity</h4>
              <div className="task-activity-list">
                <div className="task-activity-item">
                  <span className="user-avatar tiny" style={{ background: creatorColor }}>
                    {creatorInitials}
                  </span>
                  <div>
                    <span className="activity-meta">{creatorName}</span>
                    <span style={{ color: 'var(--text-muted)' }}> · Task created</span>
                  </div>
                  <span className="activity-time">{isEdit ? relTime(createdAt) : 'just now'}</span>
                </div>
              </div>
            </div>
          </div>

          <footer className="drawer-foot">
            <div>{error && <span className="badge urgent">{error}</span>}</div>
            <div
              className="drawer-foot-right"
              style={{ display: 'flex', gap: 8, alignItems: 'center' }}
            >
              {readOnly ? (
                <>
                  <span className="muted">View only. You can see this task but not change it.</span>
                  <button className="btn" onClick={onClose}>
                    Close
                  </button>
                </>
              ) : isEdit ? (
                <>
                  <button
                    className="btn task-complete-btn"
                    onClick={completeTask}
                    disabled={busy || status === 'done'}
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    {status === 'done' ? 'Completed' : 'Complete Task'}
                  </button>
                  <button className="btn" onClick={del} disabled={busy}>
                    {fresh ? (
                      'Discard Task'
                    ) : (
                      <>
                        <ArchiveIcon /> Archive Task
                      </>
                    )}
                  </button>
                  <button className="btn primary" onClick={() => finishClose(true)} disabled={busy}>
                    {busy ? 'Saving…' : 'Save'}
                  </button>
                </>
              ) : (
                <>
                  <button className="btn" onClick={onClose} disabled={busy}>
                    Cancel
                  </button>
                  <button className="btn primary" onClick={create} disabled={!title.trim() || busy}>
                    {busy ? 'Creating…' : 'Create task'}
                  </button>
                </>
              )}
            </div>
          </footer>
        </div>
      </div>
      {fillForm && (
        <TaskFormFillModal
          form={fillForm}
          onClose={() => setFillForm(null)}
          onSave={saveFormFill}
        />
      )}
    </>
  );
}

/** Tasks surface (V27) — counted filter tabs, sort, list/board, click-to-edit, on the live /tasks API. */
/** Tasks surface (V34) — command bar (search · Linked-to · View · sort), filter
 *  chips, and the List grid, on the live /tasks API. Board keeps the V27 columns;
 *  Calendar is a month grid. Status is binary (Complete/Open) — see plan §8. */
/** alertshub#7: `openTaskId` opens that task's drawer on arrival (an Alerts Hub card's link). */
export function TasksTab({ openTaskId = null }: { openTaskId?: string | null } = {}) {
  const branding = useBranding(); // ehr#492
  const { user } = useSession();
  const [filter, setFilter] = useState<WsFilter>(TASK_DEFAULT_FILTER);
  const [pins, setPins] = useState<WsFilter[]>(TASK_PIN_DEFAULTS);
  const [gridOpen, setGridOpen] = useState(false);
  const [linkGroup, setLinkGroup] = useState<LinkGroup>('all');
  const [view, setView] = useState<'list' | 'board' | 'calendar'>('list');
  const [sort, setSort] = useState<Sort>('due_asc');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [calMonth, setCalMonth] = useState<{ y: number; m: number } | null>(null);
  const [drawer, setDrawer] = useState<{ open: boolean; editId: string | null; fresh?: boolean }>(
    () => (openTaskId ? { open: true, editId: openTaskId } : { open: false, editId: null }),
  );
  // ehr#659: ONE query for every count and list on the page. It read open and done tasks in two
  // requests, so the chips mixed two moments, and one that failed (a rate-limit blip) left its half
  // empty with no error: totals of 0, 8 and 11 in three tabs. Open vs closed is split here with
  // isOpenTask, the rule the Dashboard tile and the sidebar badge use.
  const tasksF = useFetch<Task[]>(() => api.get('/tasks'), []);
  const reload = tasksF.reload;
  // ehr#341 - a teammate's task (the sidebar's live check of the tasks stamp, every 3 s, announces
  // it): fetch again in the background. ehr#659: and on coming back to the tab (and every 20 s),
  // as the Dashboard does, so a tab that missed a change or a load catches up by itself.
  const refreshRef = useRef(() => {});
  refreshRef.current = tasksF.refresh;
  useEffect(() => {
    const onRemote = () => refreshRef.current();
    window.addEventListener(TASKS_REMOTE, onRemote);
    return () => window.removeEventListener(TASKS_REMOTE, onRemote);
  }, []);
  useLiveRefresh(tasksF.refresh);
  // ehr#623: names for clinicians too (they can't read /settings/users).
  const usersF = useFetch<OrgUser[]>(() => fetchNoteUsers(), []);
  const contactsF = useFetch<{ items: LinkContact[] }>(() => api.get('/contacts?limit=500'), []);

  const all = tasksF.data ?? [];
  const openList = all.filter((t) => !isClosed(t));
  const doneList = all.filter(isClosed);
  const contactById = new Map((contactsF.data?.items ?? []).map((c) => [c.id, c]));

  const everything = [...openList, ...doneList];
  const [aw, as] = filter;
  const countWs = (w: TaskWho, s: TaskState) =>
    everything.filter((t) => taskMatchesWs(t, user?.id, w, s)).length;
  const base = everything.filter((t) => taskMatchesWs(t, user?.id, aw, as));

  // Chip pins (session-only): apply / pin-toggle / reset. "All" (anyone·any) is
  // the way back to everything and can never be unpinned.
  const isPinned = (w: TaskWho, s: TaskState) => pins.some((p) => p[0] === w && p[1] === s);
  const activePinned = isPinned(aw, as);
  const applyFilter = (w: TaskWho, s: TaskState) => {
    setFilter([w, s]);
    setGridOpen(false);
  };
  const togglePin = (w: TaskWho, s: TaskState) => {
    if (w === 'anyone' && s === 'any') return; // never unpin All
    setPins((ps) =>
      ps.some((p) => p[0] === w && p[1] === s)
        ? ps.filter((p) => !(p[0] === w && p[1] === s))
        : [...ps, [w, s]],
    );
  };
  const filterRowRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!gridOpen) return;
    const h = (e: MouseEvent) => {
      if (filterRowRef.current && !filterRowRef.current.contains(e.target as Node))
        setGridOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [gridOpen]);

  // Link-group classification via the linked contact's category: Clients vs
  // Partners (referral sources + staff). Mockup's prospect/partner split mapped
  // onto this EHR's contact categories (see plan §8).
  const groupOf = (t: Task): LinkGroup | null => {
    if (!t.contactId) return null;
    const c = contactById.get(t.contactId);
    if (!c) return null;
    return c.category === 'client' ? 'client' : 'partner';
  };
  const matchesSearch = (t: Task) =>
    !search.trim() || (t.title || '').toLowerCase().includes(search.trim().toLowerCase());
  const bySearch = base.filter(matchesSearch);
  const visible = bySearch.filter((t) => linkGroup === 'all' || groupOf(t) === linkGroup);
  const linkCounts: Record<LinkGroup, number> = {
    all: bySearch.length,
    client: bySearch.filter((t) => groupOf(t) === 'client').length,
    partner: bySearch.filter((t) => groupOf(t) === 'partner').length,
  };

  const shown = [...visible].sort((a, b) => {
    if (sort === 'title') return a.title.localeCompare(b.title);
    if (sort === 'priority') return PRIORITY_RANK[a.priority]! - PRIORITY_RANK[b.priority]!;
    // due_asc / due_desc — nulls always last
    const av = a.dueAt ? new Date(a.dueAt).getTime() : null;
    const bv = b.dueAt ? new Date(b.dueAt).getTime() : null;
    if (av === null && bv === null) return 0;
    if (av === null) return 1;
    if (bv === null) return -1;
    return sort === 'due_desc' ? bv - av : av - bv;
  });

  const openEditor = (id: string) => setDrawer({ open: true, editId: id });
  // Create-on-open (V27): a fresh task is created immediately, then the drawer
  // opens on it — so it has an id, a Delete/Close footer, and working forms/notes.
  const newTask = () => {
    api
      .post<{ id: string }>('/tasks', { title: '', priority: 'normal', draft: true })
      .then((t) => setDrawer({ open: true, editId: t.id, fresh: true }))
      .catch((e: unknown) =>
        showError(e instanceof ApiError ? e.message : 'Could not create task'),
      );
  };

  async function toggle(t: TaskView) {
    try {
      const closed = !isOpenTask(t);
      await api.post(`/tasks/${t.id}/${closed ? 'reopen' : 'done'}`);
      reload();
    } catch (e) {
      showError(errorText(e, 'Could not update the task.'));
    }
  }

  const toggleSel = (id: string) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allShownSel = shown.length > 0 && shown.every((t) => selected.includes(t.id));

  // Bulk actions — the API has no bulk endpoint, so each acts per-id then reloads.
  const runBulk = async (fn: (id: string) => Promise<unknown>) => {
    for (const id of [...selected]) {
      try {
        await fn(id);
      } catch {
        /* skip failures, keep going */
      }
    }
    setSelected([]);
    reload();
  };
  const bulkComplete = () =>
    runBulk((id) => {
      const t = everything.find((x) => x.id === id);
      return t && !isClosed(t) ? api.post(`/tasks/${id}/done`) : Promise.resolve();
    });
  const bulkReassign = (userId: string) =>
    runBulk((id) => api.patch(`/tasks/${id}`, { assigneeIds: [userId] }));
  const bulkPriority = (priority: string) =>
    runBulk((id) => api.patch(`/tasks/${id}`, { priority }));
  // ehr#736 (Jay 10-31): archive, never delete. Show archived (under the list) restores them.
  const bulkArchive = async () => {
    if (
      !(await askConfirm({
        message: `Archive ${selected.length} task${selected.length === 1 ? '' : 's'}? Turn on Show archived to see them again or restore them.`,
        confirmLabel: 'Archive',
        danger: false,
      }))
    )
      return;
    void runBulk((id) => archiveItem({ kind: 'task', id }));
  };

  // Board drag-and-drop — dropping a card into a status column PATCHes its status.
  const moveToCol = (taskId: string, colId: TaskStatus) => {
    const t = everything.find((x) => x.id === taskId);
    if (!t || t.status === colId) return;
    api
      .patch(`/tasks/${taskId}`, { status: colId })
      .then(() => {
        reload();
      })
      .catch(() => undefined);
  };

  const contactName = (id: string | null): { name: string; group: LinkGroup } | null => {
    if (!id) return null;
    const c = contactById.get(id);
    if (!c) return null;
    return {
      name: `${c.firstName} ${c.lastName}`.trim() || '(unnamed)',
      group: c.category === 'client' ? 'client' : 'partner',
    };
  };

  const loading = tasksF.data === null && tasksF.loading;
  // ehr#659: a failed load says so, instead of chips reading 0 as if there were no tasks.
  const loadFailed = tasksF.data === null && !tasksF.loading && tasksF.error !== null;
  const shownCount = (n: number) => (loadFailed ? '–' : n);

  return (
    <div className="tasks-page">
      <div className="tasks-header">
        <div className="tasks-title-block">
          <h1>{relabel('Tasks', branding.labels)}</h1>
          <p>
            Shared to-dos across your team. Assign work to teammates, attach to prospects, track
            what&rsquo;s due.
          </p>
        </div>
        <button className="btn primary" onClick={newTask}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          {relabel('New Task', branding.labels)}
        </button>
      </div>

      <div className="tasks-cmd">
        <div className="tasks-cmd-top">
          <label className="tasks-search">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="7" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              placeholder="Search tasks…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search tasks"
            />
            {search && (
              <button
                type="button"
                className="tasks-search-clear"
                onClick={() => setSearch('')}
                aria-label="Clear search"
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </label>

          <span className="tasks-zone">
            <span className="tasks-rule" />
            <span className="tasks-mini">Linked to</span>
            <span className="segbar" role="group" aria-label="Linked record type">
              {(
                [
                  ['all', 'Any'],
                  ['client', 'Clients'],
                  ['partner', 'Partners'],
                ] as [LinkGroup, string][]
              ).map(([v, l]) => (
                <button
                  key={v}
                  type="button"
                  aria-pressed={linkGroup === v}
                  onClick={() => setLinkGroup(v)}
                >
                  {l}
                  <span className="n">{shownCount(linkCounts[v])}</span>
                </button>
              ))}
            </span>
          </span>

          <span className="tasks-zone">
            <span className="tasks-rule" />
            <span className="tasks-mini">View</span>
            <span className="segbar" role="group" aria-label="View">
              <button
                type="button"
                aria-pressed={view === 'board'}
                onClick={() => setView('board')}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="3" width="7" height="18" rx="1.5" />
                  <rect x="14" y="3" width="7" height="11" rx="1.5" />
                </svg>
                Board
              </button>
              <button type="button" aria-pressed={view === 'list'} onClick={() => setView('list')}>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="8" y1="6" x2="21" y2="6" />
                  <line x1="8" y1="12" x2="21" y2="12" />
                  <line x1="8" y1="18" x2="21" y2="18" />
                  <line x1="3" y1="6" x2="3.01" y2="6" />
                  <line x1="3" y1="12" x2="3.01" y2="12" />
                  <line x1="3" y1="18" x2="3.01" y2="18" />
                </svg>
                List
              </button>
              <button
                type="button"
                aria-pressed={view === 'calendar'}
                onClick={() => setView('calendar')}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="4" width="18" height="18" rx="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
                Calendar
              </button>
            </span>
          </span>

          {view === 'list' && (
            <Dd
              value={sort}
              searchable={false}
              options={[
                { value: 'due_asc', label: 'Due ↑' },
                { value: 'due_desc', label: 'Due ↓' },
                { value: 'priority', label: 'Priority' },
                { value: 'title', label: 'Title A–Z' },
              ]}
              onChange={(v) => setSort(v as Sort)}
            />
          )}
        </div>

        <div className="tasks-filter-row" ref={filterRowRef}>
          <span className="tasks-mini">Filter</span>
          <span className="tasks-strip">
            {pins.map(([w, s]) => {
              const on = w === aw && s === as;
              const locked = w === 'anyone' && s === 'any';
              return (
                <button
                  key={wsKey(w, s)}
                  type="button"
                  className={`tasks-filter ${s === 'overdue' ? 'urgent' : ''} ${on ? 'active' : ''} ${locked ? 'locked' : ''}`}
                  onClick={() => applyFilter(w, s)}
                  title={wsLabel(w, s)}
                >
                  {wsLabel(w, s)}
                  <span className="count">{shownCount(countWs(w, s))}</span>
                  {/* ehr#223: a view is pinned and unpinned only from + more. */}
                </button>
              );
            })}
          </span>
          <span className="tasks-strip-badge">{pins.length} pinned</span>
          <button
            type="button"
            className={`filter-more ${gridOpen ? 'open' : ''} ${activePinned ? '' : 'holding'}`}
            aria-expanded={gridOpen}
            onClick={(e) => {
              e.stopPropagation();
              setGridOpen((o) => !o);
            }}
          >
            {activePinned ? (
              <>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                >
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                More
              </>
            ) : (
              <>
                {wsLabel(aw, as)}
                <span className="count">{shownCount(countWs(aw, as))}</span>
              </>
            )}
          </button>
          {gridOpen && (
            <div className="gridpop open" onClick={(e) => e.stopPropagation()}>
              <div className="gp-head">
                <h3>All combinations</h3>
                <span>click a cell to view · pin to keep it on the row</span>
              </div>
              <table className="grid">
                <thead>
                  <tr>
                    <th />
                    {TASK_STATE.map((s) => (
                      <th key={s.id}>{s.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {TASK_WHO.map((w) => (
                    <tr key={w.id}>
                      <th className="rowh">{w.label}</th>
                      {TASK_STATE.map((s) => {
                        const n = countWs(w.id, s.id);
                        const on = w.id === aw && s.id === as;
                        const pinned = isPinned(w.id, s.id);
                        return (
                          <td key={s.id}>
                            <button
                              type="button"
                              className={`cell ${on ? 'on' : ''} ${n === 0 ? 'zero' : ''}`}
                              onClick={() => applyFilter(w.id, s.id)}
                              title={`${wsLabel(w.id, s.id)} — ${n} task${n === 1 ? '' : 's'}`}
                            >
                              {shownCount(n)}
                              {pinned && <span className="dot" />}
                              <span
                                className={pinned ? 'pin is-pinned' : 'pin'}
                                title={
                                  pinned
                                    ? 'Pinned: click to remove from your row'
                                    : 'Pin to your filter row'
                                }
                                role="button"
                                aria-label={`${pinned ? 'Unpin' : 'Pin'} ${wsLabel(w.id, s.id)}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  togglePin(w.id, s.id);
                                }}
                              >
                                <svg
                                  viewBox="0 0 24 24"
                                  fill={pinned ? 'currentColor' : 'none'}
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <path d="M12 17v5" />
                                  <path d="M9 10.76V4h6v6.76a2 2 0 0 0 .55 1.38L18 15H6l2.45-2.86A2 2 0 0 0 9 10.76z" />
                                </svg>
                              </span>
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="gp-foot">
                Click a pin to add the view to your filter row, again to remove it
                <button
                  type="button"
                  className="gp-reset"
                  onClick={() => setPins(TASK_PIN_DEFAULTS)}
                >
                  Reset to default
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {view === 'list' && selected.length > 0 && (
        <div className="task-bulk-bar">
          <span className="task-bulk-count">{selected.length} selected</span>
          <button type="button" className="btn ghost-sm" onClick={bulkComplete}>
            Mark Complete
          </button>
          <Dd
            value=""
            options={[
              { value: '', label: 'Reassign to…' },
              ...(usersF.data ?? []).map((u) => ({ value: u.id, label: uName(u) })),
            ]}
            onChange={(v) => {
              if (v) bulkReassign(v);
            }}
          />
          <Dd
            value=""
            searchable={false}
            options={[{ value: '', label: 'Set priority…' }, ...PRIORITY_OPTS]}
            onChange={(v) => {
              if (v) bulkPriority(v);
            }}
          />
          <button type="button" className="btn ghost-sm" onClick={bulkArchive}>
            Archive
          </button>
          <button
            type="button"
            className="btn ghost-sm"
            style={{ marginLeft: 'auto' }}
            onClick={() => setSelected([])}
          >
            Clear
          </button>
        </div>
      )}

      {loading ? (
        <div className="tasks-empty">
          <p>Loading tasks…</p>
        </div>
      ) : loadFailed ? (
        <div className="tasks-empty" role="alert">
          <h3>Could not load tasks</h3>
          <p>
            <button className="btn" onClick={reload}>
              Try again
            </button>
          </p>
        </div>
      ) : shown.length === 0 ? (
        <div className="tasks-empty">
          <h3>{as === 'done' ? 'No completed tasks' : 'No tasks'}</h3>
          <p>{search ? 'Nothing matches your search.' : 'Create a task to get started.'}</p>
        </div>
      ) : view === 'calendar' ? (
        (() => {
          const today = new Date();
          const cm = calMonth ?? { y: today.getFullYear(), m: today.getMonth() };
          const first = new Date(cm.y, cm.m, 1);
          const startDay = first.getDay();
          const daysInMonth = new Date(cm.y, cm.m + 1, 0).getDate();
          const monthLabel = first.toLocaleDateString('en-US', {
            month: 'long',
            year: 'numeric',
          });
          const key = (y: number, m: number, d: number) =>
            `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
          const byDay: Record<string, Task[]> = {};
          shown.forEach((t) => {
            if (!t.dueAt) return;
            const d = new Date(t.dueAt);
            (byDay[key(d.getFullYear(), d.getMonth(), d.getDate())] ??= []).push(t);
          });
          const todayKey = key(today.getFullYear(), today.getMonth(), today.getDate());
          const cells: (number | null)[] = [];
          for (let i = 0; i < startDay; i++) cells.push(null);
          for (let d = 1; d <= daysInMonth; d++) cells.push(d);
          while (cells.length % 7 !== 0) cells.push(null);
          const dow = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
          const shift = (delta: number) => {
            let m = cm.m + delta;
            let y = cm.y;
            if (m < 0) {
              m = 11;
              y--;
            }
            if (m > 11) {
              m = 0;
              y++;
            }
            setCalMonth({ y, m });
          };
          const navBtn = { padding: '4px 10px', minHeight: 0 } as const;
          return (
            <div className="task-cal">
              <div className="task-cal-head">
                <button
                  className="btn"
                  style={navBtn}
                  onClick={() => shift(-1)}
                  aria-label="Previous month"
                >
                  ‹
                </button>
                <h3>{monthLabel}</h3>
                <button
                  className="btn"
                  style={navBtn}
                  onClick={() => shift(1)}
                  aria-label="Next month"
                >
                  ›
                </button>
                <button
                  className="btn"
                  style={{ ...navBtn, marginLeft: 8 }}
                  onClick={() => setCalMonth(null)}
                >
                  Today
                </button>
              </div>
              <div className="task-cal-grid">
                {dow.map((d) => (
                  <div key={d} className="task-cal-dow">
                    {d}
                  </div>
                ))}
                {cells.map((d, i) => {
                  if (d === null) return <div key={i} className="task-cal-cell empty" />;
                  const k = key(cm.y, cm.m, d);
                  const dayTasks = byDay[k] ?? [];
                  return (
                    <div key={i} className={`task-cal-cell ${k === todayKey ? 'today' : ''}`}>
                      <div className="task-cal-date">{d}</div>
                      <div className="task-cal-tasks">
                        {dayTasks.slice(0, 4).map((t) => {
                          const col = PRIORITY_COLS.find((c) => c.key === t.priority);
                          return (
                            <button
                              key={t.id}
                              className={`task-cal-pill ${isClosed(t) ? 'done' : ''}`}
                              style={{
                                borderLeft: `3px solid ${col ? col.dot : 'var(--text-muted-2)'}`,
                              }}
                              onClick={() => openEditor(t.id)}
                            >
                              {(t.title || '(untitled)').slice(0, 22)}
                            </button>
                          );
                        })}
                        {dayTasks.length > 4 && (
                          <span className="task-cal-more">+{dayTasks.length - 4} more</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()
      ) : view === 'board' ? (
        <TaskBoardView
          tasks={shown}
          users={usersF.data ?? []}
          contactName={contactName}
          onOpen={openEditor}
          onMove={moveToCol}
        />
      ) : (
        <TaskListView
          tasks={shown}
          users={usersF.data ?? []}
          contactName={contactName}
          onOpen={openEditor}
          onToggle={toggle}
          selectable
          selected={selected}
          onToggleSel={toggleSel}
          onToggleAll={() => setSelected(allShownSel ? [] : shown.map((t) => t.id))}
          allSelected={allShownSel}
        />
      )}
      <ArchivedTasks onOpen={openEditor} onRestored={reload} />

      {drawer.open && (
        <TaskDrawer
          editId={drawer.editId}
          fresh={drawer.fresh}
          onClose={() => setDrawer({ open: false, editId: null })}
          onSaved={() => {
            setDrawer({ open: false, editId: null });
            reload();
          }}
        />
      )}
    </div>
  );
}
